import { useCallback, useEffect, useState } from "react";
import api from "../../services/api"; // matches Home.jsx's sibling-folder depth (pages/CarryForwardManagement/ -> pages/ -> src/ -> services/)

/**
 * Fetches the count of pending carry-forward sessions for a branch, for use
 * in a sidebar badge (or anywhere else that needs the true total, not just
 * whatever page/rows happen to be loaded elsewhere).
 *
 * Backend guarantees { count: number } via CarryForwardPendingCountResponse,
 * so no shape-guessing is needed here.
 */
export default function useCarryForwardPendingCount(branchId, { pollIntervalMs = 60000 } = {}) {
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCount = useCallback(async () => {
    if (!branchId) return;
    try {
      const response = await api.get(
        `app/patient/carry-forward/sessions/count?branch_id=${branchId}&status=PENDING`,
      );
      setPendingCount(response?.data?.count ?? 0);
      setError(null);
    } catch (err) {
      console.error("Failed to load carry-forward pending count:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchCount();
    if (!pollIntervalMs) return undefined;
    const interval = setInterval(fetchCount, pollIntervalMs);
    return () => clearInterval(interval);
  }, [fetchCount, pollIntervalMs]);

  return { pendingCount, loading, error, refetch: fetchCount };
}